import type { Document } from "../document/Document";
import type { GroupLayer, Layer } from "../document/Layer";
import type { Command } from "./Command";
import { UngroupCommand } from "./UngroupCommand";

// Wrap a set of sibling layers in a new group layer. The group is placed at
// the position of the topmost selected layer in its (shared) parent.
//
// Constraints / behavior:
// - All target layers must share a parent for the operation to make sense.
//   If they don't, we group only those that share the parent of the *first*
//   layer found, and leave the rest in place. (Photoshop's behavior.)
// - Invert is UngroupCommand for the new group id.

export class GroupLayersCommand implements Command {
  readonly kind = "group-layers";
  constructor(
    private readonly layerIds: readonly string[],
    private readonly groupId: string,
    private readonly groupName: string,
  ) {}

  apply(doc: Document): Document {
    if (this.layerIds.length === 0) return doc;
    const ids = new Set(this.layerIds);
    const result = groupAt(doc.layers, ids, this.groupId, this.groupName, null);
    if (!result.applied) return doc;
    return { ...doc, layers: result.layers };
  }

  invert(_before: Document, _after: Document): Command {
    return new UngroupCommand(this.groupId);
  }
}

interface GroupResult {
  layers: Layer[];
  applied: boolean;
}

// Walk to the first list that contains any of the target ids; group those
// in-place at the topmost matching index.
function groupAt(
  layers: readonly Layer[],
  ids: Set<string>,
  groupId: string,
  groupName: string,
  _parentId: string | null,
): GroupResult {
  // Are any of our target ids in this list?
  const matchedIndices: number[] = [];
  for (let i = 0; i < layers.length; i++) {
    if (ids.has(layers[i]!.id)) matchedIndices.push(i);
  }

  if (matchedIndices.length > 0) {
    const selected = matchedIndices.map((i) => layers[i]!);
    // Topmost = highest index in bottom-first list.
    const topmostIndex = matchedIndices[matchedIndices.length - 1]!;
    const group: GroupLayer = {
      kind: "group",
      id: groupId,
      name: groupName,
      visible: true,
      opacity: 1,
      fillOpacity: 1,
      blendMode: "normal",
      x: 0,
      y: 0,
      lockTransparency: false,
      lockImage: false,
      lockPosition: false,
      lockAll: false,
      clippedToBelow: false,
      children: selected,
    };
    const remaining: Layer[] = [];
    let insertedAt = -1;
    for (let i = 0; i < layers.length; i++) {
      if (matchedIndices.includes(i)) {
        if (i === topmostIndex) {
          insertedAt = remaining.length;
          remaining.push(group);
        }
        continue;
      }
      remaining.push(layers[i]!);
    }
    if (insertedAt < 0) remaining.push(group);
    return { layers: remaining, applied: true };
  }

  // Recurse into groups (but only one match site — first hit wins).
  const out: Layer[] = [];
  let applied = false;
  for (const l of layers) {
    if (!applied && l.kind === "group") {
      const inner = groupAt(l.children, ids, groupId, groupName, l.id);
      if (inner.applied) {
        out.push({ ...l, children: inner.layers });
        applied = true;
        continue;
      }
    }
    out.push(l);
  }
  return { layers: out, applied };
}
